'use client';

import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { motion, isMotionComponent, type HTMLMotionProps } from 'motion/react';
import { cn } from '@/lib/utils';
type AnyProps = Record<string, unknown>;
type DOMMotionProps<T extends HTMLElement = HTMLElement> = Omit<HTMLMotionProps<keyof HTMLElementTagNameMap>, 'ref'> & {
  ref?: React.Ref<T>;
};
type WithAsChild<Base extends object> = (Base & {
  asChild: true;
  children: React.ReactElement;
}) | (Base & {
  asChild?: false | undefined;
});
type SlotProps<T extends HTMLElement = HTMLElement> = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children?: any;
} & DOMMotionProps<T>;
function mergeRefs<T>(...refs: (React.Ref<T> | undefined)[]): React.RefCallback<T> {
  return node => {
    refs.forEach(ref => {
      if (!ref) return;
      if (typeof ref === 'function') {
        ref(node);
      } else {
        (ref as React.RefObject<T | null>).current = node;
      }
    });
  };
}
function mergeProps<T extends HTMLElement>(childProps: AnyProps, slotProps: DOMMotionProps<T>): AnyProps {
  const merged: AnyProps = {
    ...childProps,
    ...slotProps
  };
  if (childProps.className || slotProps.className) {
    merged.className = cn(childProps.className as string, slotProps.className as string);
  }
  if (childProps.style || slotProps.style) {
    merged.style = {
      ...(childProps.style as React.CSSProperties),
      ...(slotProps.style as React.CSSProperties)
    };
  }
  return merged;
}
function Slot(t0) {
  const $ = _c(18);
  let Base;
  let childRef;
  let ref;
  let t1;
  let t2;
  if ($[0] !== t0) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const {
        children,
        ref: t3,
        ...props
      } = t0;
      ref = t3;
      let t4;
      if ($[6] !== children.type) {
        t4 = typeof children.type === "object" && children.type !== null && isMotionComponent(children.type);
        $[6] = children.type;
        $[7] = t4;
      } else {
        t4 = $[7];
      }
      const isAlreadyMotion = t4;
      let t5;
      if ($[8] !== children.type || $[9] !== isAlreadyMotion) {
        t5 = isAlreadyMotion ? children.type as React.ElementType : motion.create(children.type as React.ElementType);
        $[8] = children.type;
        $[9] = isAlreadyMotion;
        $[10] = t5;
      } else {
        t5 = $[10];
      }
      Base = t5;
      if (!React.isValidElement(children)) {
        t2 = null;
        break bb0;
      }
      const {
        ref: t6,
        ...childProps
      } = children.props as AnyProps;
      childRef = t6;
      t1 = mergeProps(childProps, props);
    }
    $[0] = t0;
    $[1] = Base;
    $[2] = childRef;
    $[3] = ref;
    $[4] = t1;
    $[5] = t2;
  } else {
    Base = $[1];
    childRef = $[2];
    ref = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  const mergedProps = t1;
  const t3 = childRef as React.Ref<T>;
  let t4;
  if ($[11] !== ref || $[12] !== t3) {
    t4 = mergeRefs(t3, ref);
    $[11] = ref;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== Base || $[15] !== mergedProps || $[16] !== t4) {
    t5 = <Base {...mergedProps} ref={t4} />;
    $[14] = Base;
    $[15] = mergedProps;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  return t5;
}
export { Slot, type SlotProps, type WithAsChild, type DOMMotionProps, type AnyProps };